import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import FreeSampleHeader from './FreeSampleHeader';

interface FreeSampleConsentStepProps {
  laboratoryName: string;
  optoutUrl?: string;
  termsUrl?: string;
  shippingConsent: boolean;
  communicationsConsent: boolean;
  onShippingConsentChange: (checked: boolean) => void;
  onCommunicationsConsentChange: (checked: boolean) => void;
  isLoading: boolean;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
  onEventEmission: (eventName: string, extraParams?: Record<string, unknown>) => void;
  validationCode?: string;
}

const FreeSampleConsentStep: React.FC<FreeSampleConsentStepProps> = ({
  laboratoryName,
  optoutUrl,
  termsUrl,
  shippingConsent,
  communicationsConsent,
  onShippingConsentChange,
  onCommunicationsConsentChange,
  isLoading,
  onBack,
  onConfirm,
  onClose,
  onEventEmission,
  validationCode,
}) => {
  const isMobile = useIsMobile();
  const isConfirmDisabled = !shippingConsent;
  const hasEmittedViewEventRef = useRef(false);

  useEffect(() => {
    if (hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    onEventEmission('backoffice_campaign_amostra_gratis_termos_view', { laboratory: laboratoryName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitTermsClick = (continued: boolean) => {
    onEventEmission('backoffice_campaign_amostra_gratis_termos_click', {
      codigoValidacao: validationCode,
      continouSolicitacao: continued,
    });
  };

  const handleConfirm = () => {
    emitTermsClick(true);
    onConfirm();
  };

  const handleBack = () => {
    emitTermsClick(false);
    onBack();
  };

  // O Figma anota `..._endereco_cancelar_click` aqui por engano; o nome correto é o de termos.
  const handleClose = () => {
    onEventEmission('backoffice_campaign_amostra_gratis_termos_cancelar_click');
    onClose();
  };

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      {isMobile ? (
        <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
          <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
            <LeftArrowIcon onClick={handleBack} cursor={'pointer'} />
            <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
              Voltar
            </Text>
          </Flex>
        </Box>
      ) : (
        <FreeSampleHeader onClose={handleClose} />
      )}

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Text fontSize={'18px'} fontWeight={700} color={'primary.main'}>
          Autorização de compartilhamento
        </Text>
        <Text fontSize={'14px'} color={'#343A40'} lineHeight={'17px'} paddingBottom={'18px'}>
          Para enviar sua solicitação ao laboratório, precisamos da sua autorização:
        </Text>

        <Flex flexDirection={'column'} gap={'16px'}>
          <Checkbox
            isChecked={shippingConsent}
            onChange={(e) => onShippingConsentChange(e.target.checked)}
            size="md"
            alignItems={'flex-start'}
          >
            <Box ml={'8px'} flex={1}>
              <Text fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                Concordo em receber amostras grátis de medicamentos e autorizo a Mevo a compartilhar meus dados com o{' '}
                <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                  {laboratoryName || '[Nome do laboratório]'}
                </Text>{' '}
                para essa finalidade.
              </Text>
              <Text fontSize={'14px'} color={'#343A40'} lineHeight={'20px'} mt={'8px'}>
                Ao confirmar, você está ciente de que:
              </Text>
              <Box as="ul" pl={'20px'} mt={'8px'} mb={0} listStyleType={'disc'} listStylePosition={'outside'}>
                <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                  Nome, CPF, CRM, contato e endereço informado serão compartilhados com o laboratório;
                </Text>
                <Text as="li" fontSize={'14px'} color={'#343A40'} mb={'4px'} lineHeight={'20px'}>
                  A entrega é feita pelo laboratório. A Mevo não responde pelo envio, prazo ou disponibilidade da
                  amostra;
                </Text>
                <Text as="li" fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                  Você pode revogar este consentimento a qualquer momento, de forma simples e gratuita
                  {optoutUrl && (
                    <>
                      , em{' '}
                      <Text
                        as="a"
                        href={optoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        color={'primary.main'}
                        textDecoration={'underline'}
                      >
                        {optoutUrl}
                      </Text>
                    </>
                  )}
                  .
                </Text>
              </Box>
            </Box>
          </Checkbox>

          <Checkbox
            isChecked={communicationsConsent}
            onChange={(e) => onCommunicationsConsentChange(e.target.checked)}
            size="md"
            alignItems={'flex-start'}
          >
            <Text fontSize={'14px'} color={'#343A40'} ml={'8px'} lineHeight={'20px'}>
              Quero receber avisos do laboratório{' '}
              <Text as="span" color={'primary.main'} textDecoration={'underline'}>
                {laboratoryName || '[Nome do laboratório]'}
              </Text>{' '}
              sobre novas amostras e programas para prescritores.
              <Text as="span" color={'#6C757D'}>
                {' '}(opcional)
              </Text>
            </Text>
          </Checkbox>

          {termsUrl && (
            <Text
              as="a"
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              fontSize={'14px'}
              fontWeight={700}
              color={'primary.main'}
              textDecoration={'underline'}
            >
              Ler o termo completo
            </Text>
          )}
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={handleBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={handleConfirm}
            isDisabled={isConfirmDisabled || isLoading}
            isLoading={isLoading}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            {...(!isLoading && { rightIcon: <RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" /> })}
          >
            {isLoading ? 'Enviando...' : 'Continuar'}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FreeSampleConsentStep;
